import {
  Fragment,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate, useParams } from 'react-router';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
  Case,
  Switch,
  When,
} from 'react-if';
import { format } from 'date-fns';
import { EmailTemplateTypes } from 'shared';
import { toast } from 'react-toastify';
import { DateTime } from 'luxon';
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
} from '@headlessui/react';
import { EllipsisVerticalIcon } from '@heroicons/react/20/solid';

import { MultiButtonCardHeader } from '../../../../common/Atoms/MultiButtonCardHeader';
import {
  useCreateEmailTemplateMutation,
  useDeleteEmailTemplateMutation,
  useDuplicateEmailTemplateMutation,
} from '../../../services/api/emailTemplates/emailTemplates';
import { Card } from '../../../../common/Atoms/Card';
import { CardContent } from '../../../../common/Atoms/CardContent';
import { Badge } from '../../../../common/Atoms/Badge';
import { Dropdown } from '../../../../common/Atoms/Dropdown';
import { useEmailTemplates } from '../../../hooks/useEmailTemplates';
import { classNames } from '../../../../common/lib/classNames';

import {
  templateGroupDots,
  templateGroupFromSlug,
  templateGroupKey,
  templateGroupLabels,
  templateGroupOrder,
} from './constants';

const sortLabels = {
  updated: `Last updated`,
  name: `Name`,
  type: `Type`,
};

export function EmailTemplates() {
  const { organisationId } = useParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [ searchParams ] = useSearchParams();

  // Type filter slug set by the nested sidebar, eg .../email-templates/late-fee
  const lastSegment = pathname.split(`/`).filter(Boolean).pop();
  const typeFilter = lastSegment === `email-templates` ? null : templateGroupFromSlug(lastSegment);

  // Search query set by the nested sidebar's search box
  const query = (searchParams.get(`q`) ?? ``).trim().toLowerCase();

  const [ sortKey, setSortKey ] = useState<keyof typeof sortLabels>(`updated`);

  const basePath = `/${organisationId}/settings/email-templates`;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: emailTemplates, error } = useEmailTemplates() as any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [ createTemplate, createResult ] = useCreateEmailTemplateMutation() as any;
  const [ deleteTemplate ] = useDeleteEmailTemplateMutation();
  const [ duplicateTemplate ] = useDuplicateEmailTemplateMutation();

  useEffect(() => {
    if (createResult.isError) {
      toast.error(createResult.error.data);
    }
  }, [createResult.isError]);

  const ctaOptions = useMemo(() => {
    const customs = [
      {
        label: `Invoice Reminder Email`,
        onClick: () => handleCreate(EmailTemplateTypes.REMINDER),
      },
      {
        label: `Invoice Marked Paid Email`,
        onClick: () => handleCreate(EmailTemplateTypes.AFTER_MARKED_PAID),
      },
      {
        label: `Late Fee Email`,
        onClick: () => handleCreate(EmailTemplateTypes.LATE_FEE),
      },
      {
        label: `Discount Email`,
        onClick: () => handleCreate(EmailTemplateTypes.DISCOUNT),
      },
      {
        label: `Statement Email`,
        onClick: () => handleCreate(EmailTemplateTypes.STATEMENT_EMAIL),
      },
      {
        label: `Statement Interest Charge Email`,
        onClick: () => handleCreate(EmailTemplateTypes.STATEMENT_INTEREST_EMAIL),
      },
      {
        label: `Announcement Email`,
        onClick: () => handleCreate(EmailTemplateTypes.ANNOUNCEMENT),
      },
      {
        label: `Quote Email`,
        onClick: () => handleCreate(EmailTemplateTypes.QUOTE),
      },
      {
        label: `Payment Plan Reminder`,
        onClick: () => handleCreate(EmailTemplateTypes.PAYMENT_PLAN_REMINDER),
        new: true,
      },
    ];

    return [
      customs,
    ];
  }, [createTemplate, organisationId]);

  const sortOptions = useMemo(() => [
    Object.keys(sortLabels).map(key => ({
      label: sortLabels[key],
      onClick: () => setSortKey(key as keyof typeof sortLabels),
    })),
  ], []);

  async function handleCreate(type) {
    const created = await createTemplate({
      org: organisationId,
      type,
    }).unwrap();

    // Absolute so it resolves the same from type-filtered paths
    navigate(`${basePath}/${created.template?.id}`);
  }

  function onDelete(e, id) {
    e.preventDefault();
    e.stopPropagation();

    deleteTemplate({
      org: organisationId,
      id,
    });
  }

  function onDuplicate(e, id) {
    e.stopPropagation();

    duplicateTemplate({
      org: organisationId,
      id,
    });
  }

  const templates = useMemo(() => {
    if (!emailTemplates) return null;

    const rows = emailTemplates
      .filter(t => t.type !== EmailTemplateTypes.LEGACY_LATE_FEE)
      // Type filter set by the nested sidebar
      .filter(t => !typeFilter || templateGroupKey(t.type) === typeFilter)
      .filter(t => !query || t.name.toLowerCase().includes(query));

    return rows.sort((a, b) => {
      if (sortKey === `name`) return a.name.localeCompare(b.name);

      if (sortKey === `type`) {
        const order = templateGroupOrder.indexOf(templateGroupKey(a.type)) - templateGroupOrder.indexOf(templateGroupKey(b.type));

        return order || a.name.localeCompare(b.name);
      }

      return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
    });
  }, [emailTemplates, typeFilter, query, sortKey]);

  function renderRow(t) {
    const groupKey = templateGroupKey(t.type);
    const updatedBy = t.user ? `${t.user.first_name} ${t.user.last_name}` : `Generated`;
    const meta = [
      templateGroupLabels[groupKey],
      `Updated ${format(new Date(t.updated_at), `dd LLL yy`)} by ${updatedBy}`,
      t.children.length > 0 && `${t.children.length} variation${t.children.length === 1 ? `` : `s`}`,
    ].filter(Boolean).join(` · `);

    // Created less than 4 minutes ago
    const justAdded = DateTime.fromISO(t.created_at).diffNow().as(`minutes`) > -4;

    return (
      <li
        key={ t.id }
        onClick={ () => navigate(`${basePath}/${t.id}`) }
        className={ `group flex cursor-pointer items-center gap-4 px-1 py-3.5 hover:bg-gray-50` }
      >
        <span className={ classNames(`h-2 w-2 flex-none rounded-full`, templateGroupDots[groupKey]) } />
        <div className={ `min-w-0 flex-1` }>
          <div className={ `flex items-center gap-2` }>
            <p className={ `truncate text-sm font-semibold leading-6 text-gray-900` }>{ t.name }</p>
            <When condition={ justAdded }>
              <Badge color={ `green` }
                message={ `Recently Created` } />
            </When>
          </div>
          <p className={ `truncate text-xs leading-5 text-gray-500` }>{ meta }</p>
        </div>
        <When condition={ t.policies.length > 0 }>
          <Badge color={ `green` }
            message={ `Used in ${t.policies.length} ${t.policies.length === 1 ? `policy` : `policies`}` } />
        </When>
        <button
          type={ `button` }
          onClick={ e => {
            e.stopPropagation();
            navigate(`${basePath}/${t.id}`);
          } }
          className={ `rounded-md border border-gray-200 px-3 py-1 text-xs font-semibold text-paidniceGreen-700 hover:bg-gray-100` }
        >
          { `Edit` }
        </button>
        <button
          type={ `button` }
          onClick={ e => onDuplicate(e, t.id) }
          className={ `rounded-md border border-gray-200 px-3 py-1 text-xs font-semibold text-gray-600 hover:bg-gray-100` }
        >
          { `Duplicate` }
        </button>
        <Menu as={ `div` }
          className={ `relative flex-none` }>
          <MenuButton
            onClick={ e => e.stopPropagation() }
            className={ `-m-2.5 block p-2.5 text-gray-500 hover:text-gray-900` }
          >
            <span className={ `sr-only` }>{ `Open options` }</span>
            <EllipsisVerticalIcon className={ `h-5 w-5` }
              aria-hidden={ `true` } />
          </MenuButton>
          <Transition
            as={ Fragment }
            enter={ `transition ease-out duration-100` }
            enterFrom={ `transform opacity-0 scale-95` }
            enterTo={ `transform opacity-100 scale-100` }
            leave={ `transition ease-in duration-75` }
            leaveFrom={ `transform opacity-100 scale-100` }
            leaveTo={ `transform opacity-0 scale-95` }
          >
            <MenuItems className={ `absolute right-0 z-10 mt-2 w-32 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-hidden` }>
              <MenuItem>
                { ({ focus }) => (
                  <a
                    href={ `#` }
                    onClick={ e => onDelete(e, t.id) }
                    className={ classNames(
                      focus ? `bg-gray-50` : ``,
                      `block px-3 py-1 text-sm leading-6 text-gray-900`,
                    ) }
                  >
                    { `Delete` }<span className={ `sr-only` }>{ `, ` }{ t.name }</span>
                  </a>
                ) }
              </MenuItem>
            </MenuItems>
          </Transition>
        </Menu>
      </li>
    );
  }

  return (
    <Card>
      <MultiButtonCardHeader
        title={ typeFilter ? templateGroupLabels[typeFilter] : `All templates` }
        description={ `Sorted by ${sortLabels[sortKey].toLowerCase()}` }
        cta={ <div className={ `flex items-center gap-2` }>
          <Dropdown
            size={ `md` }
            options={ sortOptions }
            label={ sortLabels[sortKey] }
          />
          <Dropdown
            size={ `md` }
            options={ ctaOptions }
            label={ `Create New` }
            disabled={ createResult.isLoading }
          />
        </div> }
      />
      <CardContent>
        <Switch>
          <Case condition={ error?.message }>
            <div>{ `Error: ` }{ error?.message }</div>
          </Case>

          <Case condition={ templates && templates.length === 0 }>
            <p className={ `py-8 text-center text-sm text-gray-500` }>
              { query ? `No templates match “${searchParams.get(`q`)}”` : `No templates yet` }
            </p>
          </Case>

          <Case condition={ !!templates }>
            <ul role={ `list` }
              className={ `divide-y divide-gray-100` }>
              { templates?.map(renderRow) }
            </ul>
          </Case>
        </Switch>
      </CardContent>

    </Card>
  );
}
